import type {
  CreateGuidelineResponse,
  GetQuestionGroupPageResponse,
  GetQuestionGroupResponse,
  GetSavedQuestionPageResponse,
  GetSavedQuestionResponse,
  CreatePlaybookRequest,
  CreatePlaybookResponse,
  ExportPlaybookApiArg,
  ExportPlaybookResponse,
  GetPlaybookResponse,
  GetPlaybookTemplateByIdApiArg,
  GetPlaybookTemplateResponse,
  Guideline,
  GuidelineWithId,
  ListPlaybookTemplatesResponse,
  PlaybookAccessAttributes,
  PlaybookExportData,
  PlaybookUsageAnalyticsResponse,
  SetPlaybookAccessResponse,
  UpdateGuidelineResponse,
  UpdatePlaybookRequest,
  UpdatePlaybookResponse,
  ValidateImportPlaybookResponse,
} from '~/api/atlas/typedefs.autogen';
import type { AccessGrant } from '~/features/access-control/types/AccessControl';
import type {
  PlaybookDetailAnalytics,
  PlaybookInsights,
} from '~/features/playbooks/types/PlaybookInsights';
import type { ResourceAccess } from '~/redux/api/methods/access-control/accessGrantMapper';
import type { WithRtkOpts } from '~/redux/api/types';

import { endpoints as autogenEnhancements } from '~/api/atlas/enhancements.autogen';
import { Tags } from '~/api/atlas/tags.autogen';

import { setAuthzToken } from './authzToken';
import { mapAtlasError, mapAtlasErrorToCodedIfClientError } from './mapError';
import {
  mapAccessGrantsToPlaybookAccessRequest,
  mapPlaybookAccessResponse,
} from './playbookAccess';
import { mapPlaybookDetailAnalyticsResponse } from './playbookDetailAnalytics';
import {
  mapPlaybookInsightsParams,
  mapPlaybookInsightsResponse,
  type PlaybookInsightsArg,
} from './playbookInsights';
import {
  mapQuestionGroupResponse,
  mapQuestionResponse,
} from './questionGroupMapping';
import { atlasApiRoot } from './root';

// Apply the schema-derived providesTags / invalidatesTags for any generated
// endpoints that are not explicitly overridden below.
atlasApiRoot.enhanceEndpoints({ endpoints: autogenEnhancements });

const GUIDELINES_LIST_TAG = { type: Tags.Guidelines, id: 'LIST' } as const;
const PLAYBOOKS_LIST_TAG = { type: Tags.Playbooks, id: 'LIST' } as const;
// The generated `Tags` map has no separate tag for playbook templates (the
// schema groups templates under the same OpenAPI `Playbooks` tag) — reuse the
// `Playbooks` tag type with a distinguishing id, which is exactly as isolated
// for cache invalidation purposes as the legacy `TagType.PlaybookTemplates`.
const PLAYBOOK_TEMPLATES_LIST_TAG = {
  type: Tags.Playbooks,
  id: 'TEMPLATES_LIST',
} as const;
const QUESTIONS_LIST_TAG = { type: Tags.Saved_Questions, id: 'LIST' } as const;
const QUESTION_GROUPS_LIST_TAG = {
  type: Tags.Question_Groups,
  id: 'LIST',
} as const;

/**
 * Consumer-facing atlas endpoints. These override the raw generated endpoints
 * (`overrideExisting: true`) to reproduce the exact runtime behavior of the
 * legacy hand-written guidelines/playbooks/question-groups methods: the same
 * friendly argument and result shapes, the same request/response envelopes,
 * the same error mapping (`JsonApiError`, or `CodedError` for the playbook
 * mutations that used to convert 4xx JSON:API errors), and the same cache
 * tags/invalidation the deleted `src/redux/api/endpoints/{guidelines,
 * playbooks,questionGroups}.ts` enhancer files used to declare.
 *
 * `getPlaybookInsights` is declared as a `build.query` even though the
 * operation is `POST /playbooks/analytics` — the endpoint is a filtered read,
 * and the dashboards refetch it as filters change. The generated
 * `postPlaybookAnalytics` mutation is left in place but unused: as a mutation
 * it would need a manual trigger, and its schema-derived
 * `invalidatesTags: [Playbooks LIST]` would refetch the playbooks list on
 * every dashboard load. No cache tags are declared here — the three cards
 * sharing this query already dedupe on the `{ playbookIds, timeRange }` cache
 * key, and nothing invalidates analytics.
 */
export const atlasApi = atlasApiRoot.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    // ===== Guidelines =====

    getGuideline: build.query<
      {
        data: { attributes: GuidelineWithId & { can_edit?: boolean | null } };
        meta?: { clause_acl?: { complete: boolean } } | null;
      },
      string
    >({
      query: (guidelineId) => ({ url: `guidelines/${guidelineId}` }),
      transformErrorResponse: mapAtlasError,
      providesTags: (_result, _error, guidelineId) => [
        { type: Tags.Guidelines, id: guidelineId },
      ],
    }),

    createGuideline: build.mutation<GuidelineWithId, WithRtkOpts<Guideline>>({
      query: ({ rtkQueryOptions, ...data }) => ({
        url: 'guidelines',
        method: 'post',
        body: { data: { type: 'guideline', attributes: data } },
        rtkQueryOptions,
      }),
      transformResponse: (response: CreateGuidelineResponse) =>
        response.data.attributes,
      transformErrorResponse: mapAtlasError,
      invalidatesTags: [GUIDELINES_LIST_TAG],
    }),

    updateGuideline: build.mutation<
      GuidelineWithId,
      WithRtkOpts<GuidelineWithId> & {
        affectedPlaybookIds?: string[];
      }
    >({
      query: ({
        rtkQueryOptions,
        affectedPlaybookIds: _ignored,
        ...guideline
      }) => ({
        url: `guidelines/${guideline.uuid}`,
        method: 'patch',
        body: { data: { type: 'guideline', attributes: guideline } },
        rtkQueryOptions,
      }),
      transformResponse: (response: UpdateGuidelineResponse) =>
        response.data.attributes,
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, _error, arg) => [
        { type: Tags.Guidelines, id: arg.uuid },
        GUIDELINES_LIST_TAG,
        PLAYBOOKS_LIST_TAG,
        ...(arg.affectedPlaybookIds ?? []).map((playbookId) => ({
          type: Tags.Playbooks,
          id: playbookId,
        })),
      ],
    }),

    deleteGuideline: build.mutation<
      unknown,
      WithRtkOpts<{ guidelineId: string; affectedPlaybookIds?: string[] }>
    >({
      query: ({ guidelineId, rtkQueryOptions }) => ({
        url: `guidelines/${guidelineId}`,
        method: 'delete',
        rtkQueryOptions,
      }),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, _error, arg) => [
        { type: Tags.Guidelines, id: arg.guidelineId },
        GUIDELINES_LIST_TAG,
        PLAYBOOKS_LIST_TAG,
        ...(arg.affectedPlaybookIds ?? []).map((playbookId) => ({
          type: Tags.Playbooks,
          id: playbookId,
        })),
      ],
    }),

    // ===== Playbooks =====
    // `getPlaybookInsights` is migrated below, under Playbook insights.

    getPlaybook: build.query<
      GetPlaybookResponse,
      { playbookId: string; clauseAcl?: boolean }
    >({
      query: ({ playbookId, clauseAcl }) => ({
        url: `playbooks/${playbookId}`,
        params: clauseAcl ? { 'clause-acl': true } : undefined,
      }),
      transformErrorResponse: mapAtlasError,
      providesTags: (_result, _error, arg) => [
        { type: Tags.Playbooks, id: arg.playbookId },
      ],
    }),

    // Deliberately not named `getPlaybookAnalyticsById`: reusing the generated
    // endpoint's name would make this override replace its whole definition,
    // dropping the `providesTags` the autogen enhancements attached to it.
    getPlaybookDetailAnalytics: build.query<
      PlaybookDetailAnalytics,
      { playbookId: string; pageIndex?: number; pageSize?: number }
    >({
      query: ({ playbookId, pageIndex, pageSize }) => {
        const params: Record<string, number> = {};
        if (pageIndex !== undefined && pageSize !== undefined) {
          params['most_negotiated[page][offset]'] = (pageIndex - 1) * pageSize;
          params['most_negotiated[page][limit]'] = pageSize;
        }
        return { url: `playbooks/${playbookId}/analytics`, params };
      },
      transformResponse: mapPlaybookDetailAnalyticsResponse,
      transformErrorResponse: mapAtlasError,
      // Scoped away from the playbook's own tag so saving a playbook doesn't
      // refetch analytics — editing a requirement can't change past reviews.
      providesTags: (_result, _error, arg) => [
        { type: Tags.Playbooks, id: `ANALYTICS_${arg.playbookId}` },
      ],
    }),

    createPlaybook: build.mutation<
      CreatePlaybookResponse,
      CreatePlaybookRequest
    >({
      query: (request) => ({
        url: 'playbooks',
        method: 'post',
        body: request,
      }),
      transformErrorResponse: mapAtlasErrorToCodedIfClientError,
      invalidatesTags: [PLAYBOOKS_LIST_TAG, GUIDELINES_LIST_TAG],
    }),

    updatePlaybook: build.mutation<
      UpdatePlaybookResponse,
      { playbookId: string; updatePlaybookRequest: UpdatePlaybookRequest }
    >({
      query: ({ playbookId, updatePlaybookRequest }) => ({
        url: `playbooks/${playbookId}`,
        method: 'patch',
        body: updatePlaybookRequest,
      }),
      transformErrorResponse: mapAtlasErrorToCodedIfClientError,
      invalidatesTags: (_result, _error, arg) => [
        { type: Tags.Playbooks, id: arg.playbookId },
        PLAYBOOKS_LIST_TAG,
        GUIDELINES_LIST_TAG,
      ],
    }),

    deletePlaybook: build.mutation<void, WithRtkOpts<{ playbookId: string }>>({
      query: ({ playbookId, rtkQueryOptions }) => ({
        url: `playbooks/${playbookId}`,
        method: 'delete',
        rtkQueryOptions,
      }),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: [PLAYBOOKS_LIST_TAG, GUIDELINES_LIST_TAG],
    }),

    getPlaybookAccess: build.query<ResourceAccess, { playbookId: string }>({
      query: ({ playbookId }) => ({ url: `playbooks/${playbookId}/access` }),
      transformResponse: (response: PlaybookAccessAttributes) =>
        mapPlaybookAccessResponse(response),
      transformErrorResponse: mapAtlasError,
      providesTags: (_result, _error, arg) => [
        { type: Tags.Playbooks, id: `ACCESS_${arg.playbookId}` },
      ],
    }),

    setPlaybookAccess: build.mutation<
      ResourceAccess,
      WithRtkOpts<{ playbookId: string; grants: AccessGrant[] }>
    >({
      query: ({ grants, playbookId, rtkQueryOptions }) => ({
        url: `playbooks/${playbookId}/access`,
        method: 'put',
        body: mapAccessGrantsToPlaybookAccessRequest(grants),
        rtkQueryOptions,
      }),
      transformResponse: (response: SetPlaybookAccessResponse) => {
        setAuthzToken(response.consistency_token);
        return mapPlaybookAccessResponse(response);
      },
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, _error, arg) => [
        { type: Tags.Playbooks, id: `ACCESS_${arg.playbookId}` },
        { type: Tags.Playbooks, id: arg.playbookId },
        PLAYBOOKS_LIST_TAG,
      ],
    }),

    getPlaybookTemplates: build.query<ListPlaybookTemplatesResponse, void>({
      query: () => ({ url: 'playbooks/templates' }),
      transformErrorResponse: mapAtlasError,
      providesTags: [PLAYBOOK_TEMPLATES_LIST_TAG],
    }),

    getPlaybookTemplate: build.query<
      GetPlaybookTemplateResponse,
      GetPlaybookTemplateByIdApiArg
    >({
      query: ({ templateId }) => ({
        url: `playbooks/templates/${templateId}`,
      }),
      transformErrorResponse: mapAtlasError,
      providesTags: (_result, _error, arg) => [
        { type: Tags.Playbooks, id: `TEMPLATE_${arg.templateId}` },
      ],
    }),

    exportPlaybook: build.mutation<
      ExportPlaybookResponse,
      ExportPlaybookApiArg
    >({
      query: ({ playbookId }) => ({
        url: `playbooks/${playbookId}/export`,
        method: 'post',
      }),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, _error, arg) => [
        { type: Tags.Playbooks, id: arg.playbookId },
      ],
    }),

    validateImportPlaybook: build.mutation<
      ValidateImportPlaybookResponse,
      { importData: PlaybookExportData }
    >({
      query: ({ importData }) => ({
        url: 'playbooks/import/validate',
        method: 'post',
        body: {
          data: {
            type: 'playbook-import',
            attributes: { import_data: importData },
          },
        },
      }),
      transformErrorResponse: mapAtlasErrorToCodedIfClientError,
      invalidatesTags: [PLAYBOOKS_LIST_TAG],
    }),

    // ===== Questions / Question groups =====

    getQuestion: build.query<ReturnType<typeof mapQuestionResponse>, string>({
      query: (id) => ({ url: `questions/${id}` }),
      transformResponse: (response: GetSavedQuestionResponse) =>
        mapQuestionResponse(response.data),
      transformErrorResponse: mapAtlasError,
      providesTags: (_result, _error, id) => [
        { type: Tags.Saved_Questions, id },
      ],
    }),

    getQuestions: build.query<
      ReturnType<typeof mapQuestionResponse>[],
      { pageSize?: number; pageLimit?: number; search?: string }
    >({
      query: ({ pageSize = 0, pageLimit = 0, search = '' } = {}) => ({
        url: 'questions',
        params: {
          'page[offset]': pageSize,
          'page[limit]': pageLimit,
          search,
        },
      }),
      transformResponse: (response: GetSavedQuestionPageResponse) =>
        response.data.map(mapQuestionResponse),
      transformErrorResponse: mapAtlasError,
      providesTags: [QUESTIONS_LIST_TAG],
    }),

    createQuestion: build.mutation<
      ReturnType<typeof mapQuestionResponse>,
      {
        text: string;
        scope: 'document_handler' | 'document_version' | 'all_documents';
      }
    >({
      query: (question) => ({
        url: 'questions',
        method: 'post',
        body: { data: { type: 'questions', attributes: { ...question } } },
      }),
      transformResponse: (response: GetSavedQuestionResponse) =>
        mapQuestionResponse(response.data),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, error) => (error ? [] : [QUESTIONS_LIST_TAG]),
    }),

    updateQuestion: build.mutation<
      ReturnType<typeof mapQuestionResponse>,
      {
        id: string;
        pinnedAt?: Date | null;
        text?: string;
        name?: string;
        questionGroupIds?: string[];
      }
    >({
      query: ({ id, ...question }) => {
        const attributes: {
          pinned_at?: string | null;
          text?: string;
          name?: string;
          question_group_ids?: string[];
        } = {};

        Object.entries(question).forEach(([key, value]) => {
          if (key === 'pinnedAt') {
            attributes.pinned_at = value
              ? (value as Date).toISOString().replace('Z', '')
              : null;
          }
          if (key === 'text' && value) {
            attributes.text = value as string;
          }
          if (key === 'name' && value) {
            attributes.name = value as string;
          }
          if (key === 'questionGroupIds' && value) {
            attributes.question_group_ids = value as string[];
          }
        });

        return {
          url: `questions/${id}`,
          method: 'patch',
          body: { data: { type: 'questions', attributes } },
        };
      },
      transformResponse: (response: GetSavedQuestionResponse) =>
        mapQuestionResponse(response.data),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, error) => (error ? [] : [QUESTIONS_LIST_TAG]),
    }),

    deleteQuestion: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({ url: `questions/${id}`, method: 'delete' }),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, error) => (error ? [] : [QUESTIONS_LIST_TAG]),
    }),

    resolveQuestions: build.query<
      ReturnType<typeof mapQuestionResponse>[],
      { ids: string[] }
    >({
      queryFn: async ({ ids }, _api, _extra, fetchWithBQ) => {
        const results = await Promise.allSettled(
          ids.map((id) => fetchWithBQ({ url: `questions/${id}` })),
        );

        const questions = results
          .filter(
            (result) => result.status === 'fulfilled' && !result.value.error,
          )
          .map(
            (result) =>
              (
                (result as PromiseFulfilledResult<{ data?: unknown }>).value
                  .data as GetSavedQuestionResponse
              ).data,
          );

        return { data: questions.map(mapQuestionResponse) };
      },
    }),

    getQuestionGroups: build.query<
      ReturnType<typeof mapQuestionGroupResponse>[],
      {
        pageSize?: number;
        pageLimit?: number;
        search?: string | null;
        visibility?: 'PRIVATE' | 'SHARED' | null;
      }
    >({
      query: ({
        pageSize = 0,
        pageLimit = 0,
        search = null,
        visibility = null,
      } = {}) => ({
        url: 'question-groups',
        params: {
          'page[offset]': pageSize,
          'page[limit]': pageLimit,
          search,
          visibility,
        },
      }),
      transformResponse: (response: GetQuestionGroupPageResponse) =>
        response.data.map(mapQuestionGroupResponse),
      transformErrorResponse: mapAtlasError,
      providesTags: [QUESTION_GROUPS_LIST_TAG],
    }),

    createQuestionGroup: build.mutation<
      ReturnType<typeof mapQuestionGroupResponse>,
      {
        name: string;
        questionIds: string[];
        visibility: 'OPEN' | 'PRIVATE' | 'SHARED';
        userIds: number[];
        departmentIds: number[];
        description?: string;
      }
    >({
      query: (questionGroup) => ({
        url: 'question-groups',
        method: 'post',
        body: {
          data: {
            type: 'question_groups',
            attributes: {
              name: questionGroup.name,
              description: questionGroup.description,
              question_ids: questionGroup.questionIds,
              visibility: questionGroup.visibility,
              user_ids: questionGroup.userIds,
              department_ids: questionGroup.departmentIds,
            },
          },
        },
      }),
      transformResponse: (response: GetQuestionGroupResponse) =>
        mapQuestionGroupResponse(response.data),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, error) =>
        error ? [] : [QUESTION_GROUPS_LIST_TAG],
    }),

    updateQuestionGroup: build.mutation<
      ReturnType<typeof mapQuestionGroupResponse>,
      {
        id: string;
        name?: string;
        questionIds?: string[];
        pinnedAt?: Date | null;
        description?: string;
        userIds?: number[];
        departmentIds?: number[];
        visibility?: 'PRIVATE' | 'OPEN' | 'SHARED';
      }
    >({
      query: ({ id, ...questionGroup }) => {
        const attributes: {
          pinned_at?: string | null;
          description?: string;
          name?: string;
          question_ids?: string[];
          user_ids?: number[];
          department_ids?: number[];
          visibility?: 'PRIVATE' | 'OPEN' | 'SHARED';
        } = {};

        Object.entries(questionGroup).forEach(([key, value]) => {
          if (key === 'pinnedAt') {
            attributes.pinned_at = value
              ? (value as Date).toISOString().replace('Z', '')
              : null;
          }
          if (key === 'description' && value) {
            attributes.description = value as string;
          }
          if (key === 'name' && value) {
            attributes.name = value as string;
          }
          if (key === 'questionIds' && value) {
            attributes.question_ids = value as string[];
          }
          if (key === 'userIds' && value) {
            attributes.user_ids = (
              questionGroup.visibility === 'SHARED' ? value : []
            ) as number[];
          }
          if (key === 'departmentIds' && value) {
            attributes.department_ids = (
              questionGroup.visibility === 'SHARED' ? value : []
            ) as number[];
          }
          if (key === 'visibility' && value) {
            attributes.visibility = value as 'PRIVATE' | 'OPEN' | 'SHARED';
          }
        });

        return {
          url: `question-groups/${id}`,
          method: 'patch',
          body: { data: { type: 'question_groups', attributes } },
        };
      },
      transformResponse: (response: GetQuestionGroupResponse) =>
        mapQuestionGroupResponse(response.data),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, error) =>
        error ? [] : [QUESTION_GROUPS_LIST_TAG],
    }),

    getQuestionGroupQuestions: build.query<
      ReturnType<typeof mapQuestionResponse>[],
      { id: string }
    >({
      query: ({ id }) => ({ url: `question-groups/${id}/questions` }),
      transformResponse: (response: GetSavedQuestionPageResponse) =>
        response.data.map(mapQuestionResponse),
      transformErrorResponse: mapAtlasError,
    }),

    resolveQuestionGroups: build.query<
      ReturnType<typeof mapQuestionGroupResponse>[],
      { ids: string[] }
    >({
      queryFn: async ({ ids }, _api, _extra, fetchWithBQ) => {
        const results = await Promise.all(
          ids.map((id) => fetchWithBQ({ url: `question-groups/${id}` })),
        );
        const errorResult = results.find((result) => result.error);
        if (errorResult) {
          return { error: errorResult.error };
        }
        return {
          data: results.map((result) =>
            mapQuestionGroupResponse(
              (result.data as GetQuestionGroupResponse).data,
            ),
          ),
        };
      },
    }),

    deleteQuestionGroup: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({ url: `question-groups/${id}`, method: 'delete' }),
      transformErrorResponse: mapAtlasError,
      invalidatesTags: (_result, error) =>
        error ? [] : [QUESTION_GROUPS_LIST_TAG],
    }),

    // ===== Playbook insights =====

    getPlaybookInsights: build.query<PlaybookInsights, PlaybookInsightsArg>({
      query: (arg) => ({
        url: '/playbooks/analytics',
        method: 'post',
        body: mapPlaybookInsightsParams(arg),
      }),
      transformResponse: (response: PlaybookUsageAnalyticsResponse) =>
        mapPlaybookInsightsResponse(response),
    }),
  }),
});
